import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle, AlertCircle, User, MessageSquare } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import SEO from './SEO';
import config from '../utils/config';

const API_URL = config.url;

const toHindiDigits = (str) => str.replace(/[0-9]/g, d => '०१२३४५६७८९'[d]);

export default function ContactPage() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});
  const [recaptchaToken, setRecaptchaToken] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setForm(prev => ({ ...prev, [name]: digitsOnly }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear field-specific error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    setError('');
    setSuccess('');
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!form.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!form.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'कृपया मान्य ईमेल पता दर्ज करें';
    }
    
    if (!form.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(form.phone.trim())) {
      newErrors.phone = 'केवल 10 अंकों का मोबाइल नंबर दर्ज करें (केवल अंक)';
    }
    
    if (!form.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!form.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (form.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters long';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRecaptcha = (token) => {
    setRecaptchaToken(token);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!recaptchaToken) {
      setError('Please complete the reCAPTCHA.');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch(`${API_URL}/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...form, recaptchaToken })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess(data.message);
        setForm({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
        setRecaptchaToken(''); // Clear token on successful submission
      } else {
        setError(data.error || 'Failed to send message');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'पता',
      details: toHindiDigits('सीधी सोच सीधी बात कार्यालय, नई दिल्ली - 110001'),
      color: 'bg-blue-100 text-blue-600'
    },
    {
      icon: Phone,
      title: 'फोन',
      details: <a href="tel:+919423014414" className="hover:text-green-700 transition-colors">{toHindiDigits('९४२३०१४४१४')}</a>,
      color: 'bg-green-100 text-green-600'
    },
    {
      icon: Mail,
      title: 'ईमेल',
      details: <a href="mailto:info@sidhisochsidhibaat.com" className="hover:text-orange-700 transition-colors">info@sidhisochsidhibaat.com</a>,
      color: 'bg-orange-100 text-orange-600'
    },
    {
      icon: Clock,
      title: 'कार्य समय',
      details: toHindiDigits('सोमवार - शनिवार: सुबह 9:00 - शाम 6:00'),
      color: 'bg-purple-100 text-purple-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100">
      <SEO 
        title="संपर्क"
        description="सीधी सोच सीधी बात से संपर्क करें। हमारे कार्यालय का पता, फोन नंबर, ईमेल और संपर्क फॉर्म।"
        keywords="संपर्क, पता, फोन, ईमेल, सीधी सोच सीधी बात, कार्यालय, संपर्क फॉर्म"
        url="/contact"
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-orange-600 to-orange-700 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-4 hindi-text leading-tight">
            हमसे संपर्क करें
          </h1>
          <p className="text-xl opacity-90 max-w-2xl mx-auto hindi-text">
            आपके सवालों, सुझावों और सहयोग के लिए हम यहाँ हैं। हमसे जुड़ें और बेहतर भविष्य बनाने में सहयोग करें।
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6 hindi-text">
                संपर्क जानकारी
              </h2>
              <p className="text-gray-600 text-lg mb-8 hindi-text">
                हमसे संपर्क करने के लिए नीचे दी गई जानकारी का उपयोग करें। हम आपकी सहायता के लिए तैयार हैं।
              </p>
            </div>

            <div className="space-y-6">
              {contactInfo.map((info, index) => {
                const Icon = info.icon;
                return (
                  <div key={index} className="flex items-start space-x-4">
                    <div className={`w-12 h-12 ${info.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1 hindi-text">{info.title}</h3>
                      <p className="text-gray-600">{info.details}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Map Placeholder */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 hindi-text">
                हमारा स्थान
              </h3>
              <div className="bg-gray-200 rounded-lg h-64 flex items-center justify-center">
                <div className="text-center">
                  <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500 hindi-text">मानचित्र यहाँ दिखाया जाएगा</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 hindi-text">
              संदेश भेजें
            </h2>

            {/* Status Messages */}
            {success && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <p className="text-green-700">{success}</p>
              </div>
            )}
            
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <p className="text-red-700">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    नाम *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors ${
                      errors.name ? 'border-red-300' : 'border-gray-300'
                    } placeholder:text-sm`}
                    placeholder="अपना पूरा नाम दर्ज करें"
                    style={{ fontSize: '0.95rem' }}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="w-4 h-4 inline mr-2" />
                    ईमेल *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors ${
                      errors.email ? 'border-red-300' : 'border-gray-300'
                    } placeholder:text-sm`}
                    placeholder="your@email.com"
                    style={{ fontSize: '0.95rem' }}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone className="w-4 h-4 inline mr-2" />
                  फोन नंबर
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  inputMode="numeric"
                  pattern="[0-9]{10}"
                  maxLength={10}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors placeholder:text-sm"
                  placeholder="अपना 10 अंकों का मोबाइल नंबर दर्ज करें"
                  style={{ fontSize: '0.95rem' }}
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  विषय *
                </label>
                <input
                  type="text"
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors ${
                    errors.subject ? 'border-red-300' : 'border-gray-300'
                  } placeholder:text-sm`}
                  placeholder="संदेश का विषय"
                  style={{ fontSize: '0.95rem' }}
                />
                {errors.subject && (
                  <p className="mt-1 text-sm text-red-600">{errors.subject}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MessageSquare className="w-4 h-4 inline mr-2" />
                  संदेश *
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={6}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors ${
                    errors.message ? 'border-red-300' : 'border-gray-300'
                  } placeholder:text-sm`}
                  placeholder="अपना संदेश यहाँ लिखें..."
                  style={{ fontSize: '0.95rem' }}
                />
                {errors.message && (
                  <p className="mt-1 text-sm text-red-600">{errors.message}</p>
                )}
              </div>

              <ReCAPTCHA
                sitekey="6LeXpZ8rAAAAAEjIm7QT2dc9ecIfgJYC0U5lvVXv"
                onChange={handleRecaptcha}
                className="my-4"
              />
              {!recaptchaToken && error && (
                <p className="mt-1 text-sm text-red-600">Please complete the reCAPTCHA.</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>भेज रहे हैं...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>संदेश भेजें</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
} 